import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, ServerError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { toast } from 'react-toastify';
import { NetworkError } from '@apollo/client/errors';
import '../assets/css/toast.css';

interface RequestBody {
  operationName: string;
}

function compileErrorMsg(networkError: NetworkError): string {
  if (networkError && 'result' in networkError) {
    if (typeof networkError.result === 'string') {
      return networkError.result;
    }
    if (networkError.result.errors) {
      // eslint-disable-next-line
      return networkError.result.errors[0].message;
    }
  }
  return 'Unexpected error';
}

const errorLink = onError(({ networkError }) => {
  // For handling dynamic forms validations errors.
  if (
    networkError &&
    ((networkError as ServerError)?.statusCode === 406 || (networkError as ServerError)?.statusCode === 421)
  ) {
    toast.error(`${compileErrorMsg(networkError)}`, {
      toastId: 'error-toast',
      position: 'top-right',
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      className: 'customToast',
    });
    return;
  }

  // For handling dynamic validations without toast.
  if (networkError && (networkError as ServerError)?.statusCode === 418) {
    return;
  }

  // For handling authentication errors.
  if ((networkError as ServerError)?.statusCode === 401 || (networkError as ServerError)?.statusCode === 403) {
    window.location.href = `https://${process.env.REACT_APP_AUTH_FE_BASE_URL}/login`;
    return;
  }

  // For handling custom-handled expected errors.
  if ((networkError as ServerError)?.statusCode === 409) {
    return;
  }

  if (networkError) {
    toast.error(`${networkError as unknown as string} Error message: ${compileErrorMsg(networkError)}`, {
      toastId: 'error-toast',
      position: 'bottom-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  }
});

const customFetch = (_: unknown, options: RequestInit | undefined): Promise<Response> => {
  if (!options || !options.body) {
    throw new Error('Request is not valid!');
  }
  const body = JSON.parse(options.body as string) as RequestBody;
  // Follow the naming convention for all the queries created on front-end. The name should go:
  // SERVICE_NAME_nameOfQuery . The service should also be listed here, in customFetch function.
  if (body.operationName.includes('ANNOUNCEMENT_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/announcement/Prod/graphql`, options);
  }
  if (body.operationName.includes('AAA_SERVICE')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/aaa/Prod/graphql`, options);
  }
  if (body.operationName.includes('USER_SERVICE_')) {
    return fetch(`https://${process.env.REACT_APP_API_BASE_URL}/userV2/Prod/graphqlNoAuth`, options);
  }
  throw new Error('Unknown server requested!');
};
const link = createHttpLink({ fetch: customFetch, credentials: 'include' });

const additiveLink = ApolloLink.from([errorLink, link]);

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false,
  }),
});
apolloClient.setLink(additiveLink);

export default apolloClient;
